import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import CalculationDetails from "./CalculationDetails";
export const fields = [
  ["deposit", "Deposit (AUD)"],
  ["annual_rate_pct", "Annual interest rate (%)"],
  ["term_years", "Loan term (years)"],
  ["weekly_rent", "Expected weekly rent (AUD; enter 0 if owner-occupied)"],
  ["vacancy_weeks", "Vacancy allowance (weeks per year)"],
  ["council_rates", "Annual council rates (AUD)"],
  ["water", "Annual water costs (AUD)"],
  ["insurance", "Annual insurance (AUD)"],
  ["strata", "Annual strata costs (AUD)"],
  ["maintenance_pct", "Annual maintenance (% of price)"],
  ["management_pct", "Management fee (% of collected rent)"],
  [
    "acquisition_costs",
    "Total acquisition costs: duty, legal, inspections and LMI (AUD)",
  ],
  ["contingency", "Cash contingency (AUD)"],
];
export const labels = {
  cash_needed: "Cash needed including contingency",
  loan_amount: "Loan amount",
  annual_gross_rent: "Weekly rent before vacancy",
  annual_collected_rent: "Weekly collected rent after vacancy",
  annual_operating_costs: "Annual operating costs",
  net_operating_income: "Annual net operating income",
  monthly_repayment: "Monthly loan repayment",
  annual_debt_service: "Annual loan repayments",
  annual_cashflow: "Annual pre-tax cash flow",
  weekly_cashflow: "Weekly pre-tax cash flow",
  gross_yield_pct: "Gross yield (%)",
  net_yield_pct: "Net yield (%)",
  break_even_weekly_rent: "Break-even weekly rent",
};
const money = (value) =>
  value === null
    ? "Not achievable with these inputs"
    : new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
      }).format(value);
const displayResult = (key, value) => {
  if (key.endsWith("_pct")) return `${value}%`;
  if (["annual_gross_rent", "annual_collected_rent"].includes(key))
    return `${money(value / 52)} / week`;
  return money(value);
};
export default function ScenarioPanel({
  price,
  propertyType,
  purchaseCosts,
  onResult,
  initialInputs,
  goal,
  needsPurchase = false,
}) {
  const [values, setValues] = useState({
    repayment_type: "principal_interest",
    ...(goal === "live_in"
      ? { weekly_rent: 0, vacancy_weeks: 0, management_pct: 0 }
      : {}),
    ...initialInputs,
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const latestPurchase = useRef(purchaseCosts);
  const requestVersion = useRef(0);
  latestPurchase.current = purchaseCosts;
  useEffect(() => {
    requestVersion.current += 1;
    setBusy(false);
    setResult(null);
    setError("");
  }, [purchaseCosts]);
  useEffect(() => {
    onResult?.(result);
  }, [result, onResult]);
  useEffect(
    () => () => {
      requestVersion.current += 1;
    },
    [],
  );
  const linked = purchaseCosts
    ? {
        deposit: purchaseCosts.costs.deposit,
        acquisition_costs: purchaseCosts.costs.acquisition_costs,
        contingency: purchaseCosts.costs.contingency,
      }
    : {};
  const effectiveValues = { ...values, ...linked };
  async function submit(event) {
    event.preventDefault();
    if (needsPurchase) return;
    const version = ++requestVersion.current;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const inputs = {
        price: Number(price),
        repayment_type: values.repayment_type,
      };
      fields.forEach(([key]) => {
        inputs[key] = Number(effectiveValues[key]);
      });
      const { data } = await api.post("/research/scenario", inputs);
      if (
        version === requestVersion.current &&
        latestPurchase.current === purchaseCosts
      )
        setResult({ ...data, calculated_at: new Date().toISOString() });
    } catch (e) {
      if (version !== requestVersion.current) return;
      setError(
        "Check that the deposit is no more than the price, vacancy is at most 52 weeks, percentages are at most 100 and the loan term is 1–40 years. Please try again.",
      );
    } finally {
      if (version === requestVersion.current) setBusy(false);
    }
  }
  if (["land", "house_and_land"].includes(propertyType))
    return (
      <aside className="notice">
        Construction and land ownership cash flow need a separate scenario.
        Construction costs and progress payments are not assessed in this
        preview.
      </aside>
    );
  return (
    <details
      id="cashflow-calculator"
      className="research-card calculator-panel"
      open={initialInputs ? true : undefined}
    >
      <summary>Explore purchase and ownership cash flow</summary>
      <p>
        Enter your own estimates or quotes. These inputs are assumptions, not
        market evidence. You can choose to include completed calculations when
        saving your report to your account. They are not sent to partners.
      </p>
      {goal === "live_in" && (
        <p className="source">
          Home-buyer drafts start with zero rent, vacancy and management fees.
          These are editable assumptions, not assessed facts.
        </p>
      )}
      {needsPurchase && (
        <p className="notice">
          Recalculate the copied purchase breakdown before calculating linked
          cash flow.
        </p>
      )}
      {purchaseCosts ? (
        <p>
          Deposit, acquisition costs and contingency use the purchase breakdown
          above. Edit that breakdown to change these amounts. Transfer duty is
          already included.
        </p>
      ) : (
        <p>
          Include all acquisition costs.{" "}
          <a href="https://qro.qld.gov.au/duties/transfer-duty/calculate/">
            Check transfer duty with QRO
          </a>{" "}
          before entering your total.
        </p>
      )}
      <form onSubmit={submit}>
        <fieldset disabled={busy} className="purchase-fields">
          <legend>Ownership assumptions</legend>
          <div className="form-grid">
            {fields.map(([key, label]) => (
              <label key={key}>
                {label}
                <input
                  required
                  type="number"
                  name={key}
                  min={key === "term_years" ? 1 : 0}
                  max={
                    key === "term_years"
                      ? 40
                      : key === "vacancy_weeks"
                        ? 52
                        : key.endsWith("_pct")
                          ? 100
                          : undefined
                  }
                  step={key === "term_years" ? 1 : "0.01"}
                  value={effectiveValues[key] ?? ""}
                  readOnly={Object.hasOwn(linked, key)}
                  onChange={(e) => {
                    setValues({ ...values, [key]: e.target.value });
                    setResult(null);
                  }}
                />
              </label>
            ))}
          </div>
          <label>
            Repayment type
            <select
              value={values.repayment_type}
              onChange={(e) => {
                setValues({ ...values, repayment_type: e.target.value });
                setResult(null);
              }}
            >
              <option value="principal_interest">Principal and interest</option>
              <option value="interest_only">Interest only</option>
            </select>
          </label>
          <button disabled={busy || needsPurchase}>
            {busy ? "Calculating…" : "Calculate my scenario"}
          </button>
        </fieldset>
        {error && <p role="alert">{error}</p>}
      </form>
      {result && (
        <section aria-label="Scenario results" aria-live="polite">
          <h3>Your scenario</h3>
          <p className="source">
            Calculated from your inputs · {result.calc_version}
          </p>
          <dl>
            {Object.entries(result.base).map(([key, value]) => (
              <div className="result-row" key={key}>
                <dt>{labels[key]}</dt>
                <dd>{displayResult(key, value)}</dd>
              </div>
            ))}
          </dl>
          <h3>Stress tests: annual pre-tax cash flow</h3>
          <dl>
            {Object.entries(result.stress_tests).map(([key, value]) => (
              <div className="result-row" key={key}>
                <dt>
                  {
                    {
                      rate_plus_2pct: "Interest rate +2 percentage points",
                      rent_minus_10pct: "Rent −10%",
                      vacancy_plus_4weeks: "Vacancy +4 weeks (capped at 52)",
                      repair_5000: "Additional $5,000 repair",
                    }[key]
                  }
                </dt>
                <dd>{money(value.annual_cashflow)}</dd>
              </div>
            ))}
          </dl>
          {result.limitations.map((text) => (
            <p className="source" key={text}>
              {text}
            </p>
          ))}
          <CalculationDetails result={result} />
        </section>
      )}
    </details>
  );
}
